/**
 * V205 — Fidelidad PÚBLICA (D2), sin login:
 *  - /fidelidad/<slug>      → alta por el QR del PDF (teléfono opcional, consentimiento
 *                             obligatorio con liga al aviso del negocio) → código + cartilla.
 *  - /fidelidad/c/<código>  → la CARTILLA: vista del servidor (perder la página no pierde
 *                             sellos), con el QR del código para la caja y "Agregar a
 *                             pantalla de inicio".
 * El tenant se resuelve en el backend (fns DEFINER por slug / por código). Cada negocio
 * es un programa aparte: el mismo cliente obtiene un código distinto en cada negocio.
 */
import { useEffect, useState, type ReactNode } from 'react';
import QRCode from 'qrcode';
import { API_BASE } from '../lib/api';
import { formatLoyaltyCode } from './loyaltyCode';

const ENROLL_RE = /^\/fidelidad\/([a-z0-9-]{1,100})\/?$/;
const CARD_RE = /^\/fidelidad\/c\/([A-Za-z0-9-]{8,9})\/?$/;

export const isFidelidadRoute = (pathname: string): boolean => CARD_RE.test(pathname) || ENROLL_RE.test(pathname);

async function publicJson<T>(path: string, init?: { method?: string; body?: unknown }): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: init?.method ?? 'GET',
    headers: { 'Content-Type': 'application/json' },
    body: init?.body != null ? JSON.stringify(init.body) : undefined,
  });
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) throw { status: res.status, body };
  return body as T;
}

interface ProgramInfo {
  businessName: string; offeror: string; slug: string; avisoVersion: string | null;
  campaign: { name: string; visitsRequired: number; rewardLabel: string; conditions: string; startsOn: string; endsOn: string } | null;
  partialExpiryDays: number; rules: string[];
}
interface CardInfo {
  businessName: string; slug: string; code: string; codeDisplay: string; programEnabled: boolean;
  campaignName: string | null; rewardLabel: string | null; visitsRequired: number | null; stamps: number;
  lastStampAt: string | null; partialsExpireAt: string | null; rewardsAvailable: number;
  campaignEndsOn: string | null; conditions: string | null;
  /** V206 — se negó a "ofertas según tu historial". */
  historyOptOut: boolean;
  /** V208 — el negocio vende en línea (enlace "Comprar en línea con mi código"). */
  storeEnabled?: boolean;
}

const fmtDate = (ymd?: string | null) => (ymd ? ymd.split('-').reverse().join('/') : '');
const shell = 'mx-auto w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-sm';

function Screen({ children }: { children: ReactNode }) {
  return <div className="min-h-screen bg-slate-50 px-4 py-8">{children}</div>;
}

export function FidelidadPage() {
  const path = window.location.pathname;
  const card = path.match(CARD_RE)?.[1];
  if (card) return <CardView code={card} />;
  return <EnrollView slug={path.match(ENROLL_RE)?.[1] ?? ''} />;
}

function EnrollView({ slug }: { slug: string }) {
  const [info, setInfo] = useState<ProgramInfo | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [phone, setPhone] = useState('');
  const [accept, setAccept] = useState(false);
  // V206 (plantilla v1.1) — mayoría de edad obligatoria + negativa (desmarcada = acepta).
  const [adult, setAdult] = useState(false);
  const [optOut, setOptOut] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    publicJson<ProgramInfo>(`/public/loyalty/${slug}`)
      .then((i) => { setInfo(i); document.title = `Programa de lealtad · ${i.businessName}`; })
      .catch(() => setNotFound(true));
  }, [slug]);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await publicJson<{ code: string; cardPath: string }>(`/public/loyalty/${slug}/enroll`, {
        method: 'POST', body: { phone: phone.trim() || undefined, acceptPrivacy: accept, isAdult: adult, historyOptOut: optOut },
      });
      window.location.assign(r.cardPath);
    } catch (e: any) {
      setErr(e?.body?.message || 'No se pudo completar el alta. Intenta de nuevo.');
    } finally { setBusy(false); }
  };

  if (notFound) {
    return <Screen><div className={shell}><p className="text-sm text-slate-600">Este programa de lealtad no está disponible.</p></div></Screen>;
  }
  if (!info) return <div className="grid min-h-screen place-items-center bg-slate-50 text-sm text-slate-500">Cargando…</div>;
  const k = info.campaign;
  if (!k) {
    return <Screen><div className={shell}><h1 className="font-serif text-2xl text-slate-900">{info.businessName}</h1>
      <p className="mt-2 text-sm text-slate-600">No hay una promoción vigente en este momento.</p></div></Screen>;
  }
  return (
    <Screen>
      <div className={shell}>
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700">Programa de lealtad</p>
        <h1 className="mt-1 font-serif text-2xl text-slate-900">{info.businessName}</h1>
        <p className="mt-3 rounded-xl bg-emerald-50 px-4 py-3 text-center text-lg font-semibold text-emerald-900 ring-1 ring-emerald-200">
          {k.visitsRequired} visitas = {k.rewardLabel}
        </p>
        <dl className="mt-4 space-y-2 text-sm text-slate-600">
          <div><dt className="font-medium text-slate-800">Vigencia</dt><dd>Del {fmtDate(k.startsOn)} al {fmtDate(k.endsOn)}</dd></div>
          <div><dt className="font-medium text-slate-800">Condiciones</dt><dd className="whitespace-pre-line">{k.conditions}</dd>
            <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs">{info.rules.map((r) => <li key={r}>{r}</li>)}</ul></div>
          <div><dt className="font-medium text-slate-800">Quién ofrece</dt><dd>{info.offeror}</dd></div>
        </dl>

        <label className="mt-5 block text-sm font-medium text-slate-800">
          Teléfono <span className="font-normal text-slate-500">(opcional)</span>
          <input className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" inputMode="tel" autoComplete="tel"
            value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="10 dígitos" />
        </label>
        <p className="mt-1 text-xs text-slate-500">
          Con tu teléfono, en caja pueden buscar tu cartilla si pierdes tu código. Sin teléfono y sin ticket,
          el código se pierde igual que una tarjeta de papel.
        </p>
        <label className="mt-4 flex items-start gap-2 text-sm text-slate-700">
          <input type="checkbox" className="mt-1" checked={accept} onChange={(e) => setAccept(e.target.checked)} />
          <span>He leído el <a className="text-emerald-700 underline" href={`/aviso/${info.slug}`} target="_blank" rel="noreferrer">aviso de privacidad de {info.businessName}</a> y acepto el tratamiento de mis datos para este programa.</span>
        </label>
        <label className="mt-3 flex items-start gap-2 text-sm text-slate-700">
          <input type="checkbox" className="mt-1" checked={adult} onChange={(e) => setAdult(e.target.checked)} />
          <span>Soy mayor de 18 años. <span className="text-xs text-slate-500">(El programa es solo para mayores de edad.)</span></span>
        </label>
        <label className="mt-3 flex items-start gap-2 text-sm text-slate-700">
          <input type="checkbox" className="mt-1" checked={optOut} onChange={(e) => setOptOut(e.target.checked)} />
          <span>No deseo que usen mi historial de visitas y compras para ofrecerme productos o promociones.
            <span className="block text-xs text-slate-500">Es una finalidad secundaria, no necesaria para el programa: puedes negarte sin que afecte tu participación (también después, desde tu cartilla).</span></span>
        </label>
        {err && <p className="mt-3 rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700 ring-1 ring-rose-200">{err}</p>}
        <button type="button" onClick={submit} disabled={!accept || !adult || busy}
          className="mt-4 w-full rounded-xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
          {busy ? 'Dando de alta…' : 'Obtener mi código'}
        </button>
      </div>
    </Screen>
  );
}

function CardView({ code }: { code: string }) {
  const [c, setC] = useState<CardInfo | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  const [installEvt, setInstallEvt] = useState<any>(null);
  const [showHelp, setShowHelp] = useState(false);
  // V206 — negativa posterior y baja desde la cartilla.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [actionErr, setActionErr] = useState<string | null>(null);

  useEffect(() => {
    publicJson<CardInfo>(`/public/loyalty/card/${encodeURIComponent(code)}`)
      .then((r) => { setC(r); document.title = `Mi cartilla · ${r.businessName}`; })
      .catch(() => setNotFound(true));
  }, [code]);
  useEffect(() => {
    if (c) QRCode.toDataURL(c.code, { margin: 1, width: 220 }).then(setQr).catch(() => {});
  }, [c]);
  useEffect(() => {
    const h = (e: any) => { e.preventDefault(); setInstallEvt(e); };
    window.addEventListener('beforeinstallprompt', h);
    return () => window.removeEventListener('beforeinstallprompt', h);
  }, []);

  if (deleted) {
    return <Screen><div className={shell}><h1 className="font-serif text-2xl text-slate-900">Te diste de baja</h1>
      <p className="mt-2 text-sm text-slate-600">Eliminamos tu teléfono y tu cartilla de este programa. Puedes volver a inscribirte cuando quieras.</p></div></Screen>;
  }
  if (notFound) return <Screen><div className={shell}><p className="text-sm text-slate-600">Cartilla no encontrada. Revisa el código de tu ticket.</p></div></Screen>;
  if (!c) return <div className="grid min-h-screen place-items-center bg-slate-50 text-sm text-slate-500">Cargando…</div>;

  const req = c.visitsRequired ?? 0;
  const optOutHistory = async () => {
    setActionErr(null);
    try {
      await publicJson(`/public/loyalty/card/${encodeURIComponent(c.code)}/history-opt-out`, { method: 'POST', body: {} });
      setC({ ...c, historyOptOut: true });
    } catch (e: any) { setActionErr(e?.body?.message || 'No se pudo registrar tu negativa.'); }
  };
  const deleteMe = async () => {
    setActionErr(null);
    try {
      await publicJson(`/public/loyalty/card/${encodeURIComponent(c.code)}/delete`, { method: 'POST', body: { confirm: true } });
      setDeleted(true);
    } catch (e: any) { setActionErr(e?.body?.message || 'No se pudo completar la baja.'); }
  };
  const addToHome = async () => {
    if (installEvt) { installEvt.prompt(); setInstallEvt(null); } else setShowHelp((v) => !v);
  };

  return (
    <Screen>
      <div className={shell}>
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700">Mi cartilla</p>
        <h1 className="mt-1 font-serif text-2xl text-slate-900">{c.businessName}</h1>
        {!c.programEnabled && (
          <p className="mt-2 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800 ring-1 ring-amber-200">
            El programa está en pausa. Tus recompensas ganadas se conservan.
          </p>
        )}
        <div className="mt-4 text-center">
          <p className="text-xs text-slate-500">Muestra este código en caja</p>
          <p className="font-mono text-3xl font-bold tracking-[0.2em] text-slate-900">{formatLoyaltyCode(c.code)}</p>
          {qr && <img src={qr} alt={`Código ${c.codeDisplay}`} className="mx-auto mt-2 h-40 w-40" />}
        </div>
        {c.rewardsAvailable > 0 && (
          <p className="mt-4 rounded-xl bg-amber-50 px-4 py-3 text-center text-sm font-semibold text-amber-800 ring-1 ring-amber-200">
            🎁 Tienes {c.rewardsAvailable === 1 ? 'una recompensa' : `${c.rewardsAvailable} recompensas`} para canjear. No caduca{c.rewardsAvailable === 1 ? '' : 'n'}.
          </p>
        )}
        {req > 0 && (
          <div className="mt-4">
            <p className="text-sm text-slate-700">{c.stamps} de {req} visitas · {c.rewardLabel}</p>
            <div className="mt-2 flex flex-wrap gap-1.5" aria-hidden>
              {Array.from({ length: req }).map((_, i) => (
                <span key={i} className={`h-7 w-7 rounded-full ring-1 ${i < c.stamps ? 'bg-emerald-500 ring-emerald-600' : 'bg-white ring-slate-300'}`} />
              ))}
            </div>
            {c.partialsExpireAt && c.stamps > 0 && (
              <p className="mt-2 text-xs text-slate-500">
                Tus sellos de esta cartilla vencen el {new Date(c.partialsExpireAt).toLocaleDateString('es-MX')} si no vuelves antes.
              </p>
            )}
            {c.campaignEndsOn && <p className="text-xs text-slate-500">Promoción vigente hasta el {fmtDate(c.campaignEndsOn)}.</p>}
          </div>
        )}
        {c.storeEnabled && (
          <a href={`/${c.slug}/tienda?lealtad=${encodeURIComponent(c.code)}`}
            className="mt-5 block w-full rounded-xl bg-emerald-600 px-4 py-2.5 text-center text-sm font-semibold text-white hover:bg-emerald-700">
            Comprar en línea con mi código
          </a>
        )}
        <p className="mt-2 text-center text-[11px] text-slate-500">Las recompensas se canjean solo en sucursal.</p>
        <button type="button" onClick={addToHome}
          className="mt-5 w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
          Agregar a pantalla de inicio
        </button>
        {showHelp && (
          <div className="mt-2 rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-600">
            <p><strong>iPhone:</strong> toca Compartir (□↑) y luego “Agregar a inicio”.</p>
            <p><strong>Android:</strong> abre el menú ⋮ del navegador y elige “Agregar a pantalla principal”.</p>
          </div>
        )}
        <p className="mt-4 text-[11px] text-slate-400">
          Tu cartilla vive en el sistema del negocio: si cierras esta página no pierdes tus sellos. Tu código también
          aparece en cada ticket sellado.
        </p>
        {c.conditions && <details className="mt-3 text-xs text-slate-500"><summary>Condiciones</summary><p className="mt-1 whitespace-pre-line">{c.conditions}</p></details>}
        <div className="mt-4 space-y-2 border-t border-slate-100 pt-3 text-xs text-slate-600">
          {c.historyOptOut ? (
            <p>Te negaste a recibir ofertas según tu historial.</p>
          ) : (
            <button type="button" onClick={optOutHistory} className="underline hover:text-slate-900">
              No quiero ofertas según mi historial de visitas y compras
            </button>
          )}
          {!confirmDelete ? (
            <button type="button" onClick={() => setConfirmDelete(true)} className="block text-rose-700 underline">Darme de baja del programa</button>
          ) : (
            <div className="rounded-xl bg-rose-50 p-3 ring-1 ring-rose-200">
              <p className="text-rose-800">Al darte de baja eliminamos tu teléfono y tu cartilla (sellos e historial). No se puede deshacer.</p>
              {c.rewardsAvailable > 0 && (
                <p className="mt-1 font-semibold text-rose-800">Tienes {c.rewardsAvailable === 1 ? 'una recompensa' : `${c.rewardsAvailable} recompensas`} sin canjear: dejará{c.rewardsAvailable === 1 ? '' : 'n'} de estar disponible{c.rewardsAvailable === 1 ? '' : 's'}. Te recomendamos canjearla antes.</p>
              )}
              <div className="mt-2 flex gap-2">
                <button type="button" onClick={deleteMe} className="rounded-lg bg-rose-600 px-3 py-1.5 font-semibold text-white">Sí, darme de baja</button>
                <button type="button" onClick={() => setConfirmDelete(false)} className="rounded-lg bg-white px-3 py-1.5 ring-1 ring-slate-200">Cancelar</button>
              </div>
            </div>
          )}
          {actionErr && <p className="text-rose-700">{actionErr}</p>}
        </div>
        <a href={`/aviso/${c.slug}`} className="mt-3 block text-[11px] text-slate-400 underline">Aviso de privacidad de {c.businessName}</a>
      </div>
    </Screen>
  );
}
